import { useState, useEffect, useRef, useCallback } from 'react';
import {
  submitJobDescription,
  submitSmartBuild,
  pollJDStatus,
  JDExtractedData,
  CustomInterviewListItem,
  SuggestedPipeline,
} from '../api/interviewBuilderApi';

const STORAGE_KEY = 'luci:jd_processing';
const POLL_INTERVAL_MS = 3000;

export type JDProcessingStatus = 'idle' | 'queued' | 'processing' | 'processed' | 'violation' | 'failed';
export type InterviewStatus = 'draft' | 'published' | 'archived';

interface JDProcessingState {
  status: JDProcessingStatus;
  interviewId: string | null;
  interviewStatus: InterviewStatus | null;
  extractedData: JDExtractedData | null;
  graphId: string | null;
  violations: string[];
  error: string | null;
  jobDescription: string | null;
  suggestedPipeline: SuggestedPipeline | null;
}

export type CustomisationLevel = 'low' | 'medium' | 'high';

export interface SmartBuildParams {
  roleId: string;
  responsibilityLevel?: number;
  deadline?: string;
}

interface UseJDProcessingReturn extends JDProcessingState {
  submit: (jobDescription: string, name?: string, existingInterviewId?: string, customisationLevel?: CustomisationLevel) => Promise<void>;
  smartBuild: (params: SmartBuildParams) => Promise<void>;
  retry: (jobDescription: string) => Promise<void>;
  reset: () => void;
  loadFromInterview: (interview: CustomInterviewListItem) => void;
  hydrateProcessedInterview: (interviewId: string) => Promise<void>;
  isProcessing: boolean;
  isComplete: boolean;
}

function loadPersisted(): { interviewId: string; status: string } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function persistState(interviewId: string, status: string) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ interviewId, status, startedAt: Date.now() }));
}

function clearPersisted() {
  localStorage.removeItem(STORAGE_KEY);
}

export function useJDProcessing(): UseJDProcessingReturn {
  const [state, setState] = useState<JDProcessingState>({
    status: 'idle',
    interviewId: null,
    interviewStatus: null,
    extractedData: null,
    graphId: null,
    violations: [],
    error: null,
    jobDescription: null,
    suggestedPipeline: null,
  });

  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const startPolling = useCallback((interviewId: string) => {
    stopPolling();

    const poll = async () => {
      if (!mountedRef.current) return;
      try {
        const { data } = await pollJDStatus(interviewId);

        if (!mountedRef.current) return;

        const jdStatus = data.jdProcessingStatus;
        const interviewStatus = data.interviewStatus ?? undefined;

        if (jdStatus === 'processed') {
          stopPolling();
          clearPersisted();
          setState(prev => ({
            ...prev,
            status: 'processed',
            extractedData: data.extractedData || null,
            graphId: data.graphId || null,
            jobDescription: data.jobDescription ?? prev.jobDescription,
            suggestedPipeline: data.suggestedPipeline ?? prev.suggestedPipeline,
            ...(interviewStatus && { interviewStatus }),
            error: null,
          }));
        } else if (jdStatus === 'violation') {
          stopPolling();
          clearPersisted();
          setState(prev => ({
            ...prev,
            status: 'violation',
            violations: data.violations || [],
            ...(interviewStatus && { interviewStatus }),
            error: null,
          }));
        } else if (jdStatus === 'failed') {
          stopPolling();
          clearPersisted();
          setState(prev => ({
            ...prev,
            status: 'failed',
            ...(interviewStatus && { interviewStatus }),
            error: 'Extraction failed. Click retry to try again.',
          }));
        } else {
          setState(prev => ({
            ...prev,
            status: jdStatus === 'processing' ? 'processing' : 'queued',
            ...(interviewStatus && { interviewStatus }),
            ...(data.jobDescription != null && { jobDescription: data.jobDescription }),
            ...(data.suggestedPipeline != null && { suggestedPipeline: data.suggestedPipeline }),
          }));
        }
      } catch (err) {
        console.error('[useJDProcessing] poll error:', err);
      }
    };

    poll();
    pollTimerRef.current = setInterval(poll, POLL_INTERVAL_MS);
  }, [stopPolling]);

  // Only restore in-flight polling from localStorage (survives page navigation)
  useEffect(() => {
    mountedRef.current = true;
    const persisted = loadPersisted();
    if (persisted && persisted.interviewId) {
      const { interviewId, status } = persisted;
      if (status === 'queued' || status === 'processing') {
        setState(prev => ({
          ...prev,
          status: status as JDProcessingStatus,
          interviewId,
        }));
        startPolling(interviewId);
      }
    }
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [startPolling, stopPolling]);

  const hydrateProcessedInterview = useCallback(async (interviewId: string) => {
    try {
      const { data } = await pollJDStatus(interviewId);
      if (!mountedRef.current || data.jdProcessingStatus !== 'processed') return;

      setState(prev => ({
        ...prev,
        status: 'processed',
        interviewId,
        interviewStatus: data.interviewStatus ?? prev.interviewStatus,
        extractedData: data.extractedData ?? prev.extractedData,
        graphId: data.graphId ?? prev.graphId,
        jobDescription: data.jobDescription ?? prev.jobDescription,
        suggestedPipeline: data.suggestedPipeline ?? prev.suggestedPipeline,
        error: null,
      }));
    } catch (err) {
      console.error('[useJDProcessing] hydrate error:', err);
    }
  }, []);

  const loadFromInterview = useCallback((interview: CustomInterviewListItem) => {
    stopPolling();
    const jdStatus = (interview.jdProcessingStatus || 'idle') as JDProcessingStatus;

    if (jdStatus === 'processed') {
      setState({
        status: 'processed',
        interviewId: interview.id,
        interviewStatus: (interview.status as InterviewStatus) || 'draft',
        extractedData: interview.jdExtractedData || null,
        graphId: interview.graphId || null,
        violations: [],
        error: null,
        jobDescription: interview.jobDescription || null,
        suggestedPipeline: null,
      });
      void hydrateProcessedInterview(interview.id);
    } else if (jdStatus === 'queued' || jdStatus === 'processing') {
      setState({
        status: jdStatus,
        interviewId: interview.id,
        interviewStatus: (interview.status as InterviewStatus) || 'draft',
        extractedData: null,
        graphId: null,
        violations: [],
        error: null,
        jobDescription: interview.jobDescription || null,
        suggestedPipeline: null,
      });
      startPolling(interview.id);
    } else if (jdStatus === 'violation') {
      const violations = (interview.jdExtractedData as any)?.violations || [];
      setState({
        status: 'violation',
        interviewId: interview.id,
        interviewStatus: (interview.status as InterviewStatus) || 'draft',
        extractedData: null,
        graphId: null,
        violations,
        error: null,
        jobDescription: interview.jobDescription || null,
        suggestedPipeline: null,
      });
    } else if (jdStatus === 'failed') {
      setState({
        status: 'failed',
        interviewId: interview.id,
        interviewStatus: (interview.status as InterviewStatus) || 'draft',
        extractedData: null,
        graphId: null,
        violations: [],
        error: 'Extraction failed. Click retry to try again.',
        jobDescription: interview.jobDescription || null,
        suggestedPipeline: null,
      });
    } else {
      setState({
        status: 'idle',
        interviewId: interview.id,
        interviewStatus: (interview.status as InterviewStatus) || 'draft',
        extractedData: null,
        graphId: null,
        violations: [],
        error: null,
        jobDescription: interview.jobDescription || null,
        suggestedPipeline: null,
      });
    }
  }, [stopPolling, startPolling, hydrateProcessedInterview]);

  const submit = useCallback(async (jobDescription: string, name?: string, existingInterviewId?: string, customisationLevel?: CustomisationLevel) => {
    setState(prev => ({
      ...prev,
      status: 'queued',
      extractedData: null,
      graphId: null,
      violations: [],
      error: null,
      jobDescription,
      suggestedPipeline: null,
    }));

    try {
      const { data } = await submitJobDescription({
        jobDescription,
        name,
        interviewId: existingInterviewId,
        customisationLevel: customisationLevel || 'medium',
      });
      const interviewId = data.interviewId;

      setState(prev => ({ ...prev, interviewId, status: 'queued' }));
      persistState(interviewId, 'queued');
      startPolling(interviewId);
    } catch (err: any) {
      setState(prev => ({
        ...prev,
        status: 'failed',
        error: err?.response?.data?.detail || 'Failed to submit job description',
      }));
    }
  }, [startPolling]);

  const smartBuild = useCallback(async (params: SmartBuildParams) => {
    stopPolling();
    clearPersisted();
    setState(prev => ({
      ...prev,
      status: 'queued',
      extractedData: null,
      graphId: null,
      violations: [],
      error: null,
      jobDescription: null,
      suggestedPipeline: null,
    }));

    try {
      const { data } = await submitSmartBuild({
        smartBuild: true,
        roleId: params.roleId,
        ...(params.responsibilityLevel != null && {
          responsibilityLevel: params.responsibilityLevel,
        }),
        ...(params.deadline && { deadline: params.deadline }),
      });
      const interviewId = data.interviewId;

      setState(prev => ({ ...prev, interviewId, status: 'queued' }));
      persistState(interviewId, 'queued');
      startPolling(interviewId);
    } catch (err: any) {
      setState(prev => ({
        ...prev,
        status: 'failed',
        error: err?.response?.data?.detail || 'Failed to start smart build',
      }));
    }
  }, [startPolling, stopPolling]);

  const retry = useCallback(async (jobDescription: string) => {
    if (!state.interviewId) return;

    setState(prev => ({
      ...prev,
      status: 'queued',
      error: null,
      violations: [],
    }));

    try {
      const { data } = await submitJobDescription({
        jobDescription,
        interviewId: state.interviewId,
      });
      persistState(data.interviewId, 'queued');
      startPolling(data.interviewId);
    } catch (err: any) {
      setState(prev => ({
        ...prev,
        status: 'failed',
        error: err?.response?.data?.detail || 'Retry failed',
      }));
    }
  }, [state.interviewId, startPolling]);

  const reset = useCallback(() => {
    stopPolling();
    clearPersisted();
    setState({
      status: 'idle',
      interviewId: null,
      interviewStatus: null,
      extractedData: null,
      graphId: null,
      violations: [],
      error: null,
      jobDescription: null,
      suggestedPipeline: null,
    });
  }, [stopPolling]);

  return {
    ...state,
    submit,
    smartBuild,
    retry,
    reset,
    loadFromInterview,
    hydrateProcessedInterview,
    isProcessing: state.status === 'queued' || state.status === 'processing',
    isComplete: state.status === 'processed',
  };
}
